// Thin PeerJS abstraction for online 1:1 play (ported from the reference
// games' net/peer.js — Coin-puzzle's version, itself ported from 2026).
// Game-agnostic transport: hostRoom()/joinRoom() resolve to a Session with
//   send(msg) · onMessage(fn) · onClose/onLost/onReconnect(fn) · close() · role · roomCode.
// Signalling goes through the public PeerJS Cloud broker; once the data channel
// opens, packets flow peer-to-peer. The per-game state sync layer sits on top.

import Peer, { type DataConnection } from "peerjs";
import { GAME_ID } from "../../gameId.ts";

const ROOM_PREFIX = `${GAME_ID}-`;
const ID_ALPHABET = "0123456789";

// Cost to start a 1:1 versus match. Per the reference policy the HOST only is
// charged (the guest pays nothing); deducted when the match actually starts
// (guest connected).
export const VERSUS_HOST_COST = 50;

export function generateRoomCode(len = 4): string {
  let out = "";
  for (let i = 0; i < len; i++) {
    out += ID_ALPHABET[Math.floor(Math.random() * ID_ALPHABET.length)];
  }
  return out;
}

export interface Session {
  readonly role: "host" | "guest";
  readonly roomCode: string;
  readonly peerId: string;
  readonly remoteId: string;
  send(msg: unknown): void;
  onMessage(h: (msg: unknown) => void): () => void;
  onClose(h: () => void): () => void;
  onLost(h: () => void): () => void;
  onReconnect(h: () => void): () => void;
  isOpen(): boolean;
  isLost(): boolean;
  close(): void;
  dispose(): void;
  /** @internal */
  _swapConn(newConn: DataConnection): void;
}

function makePeer(id?: string): Peer {
  return id ? new Peer(id, { debug: 1 }) : new Peer({ debug: 1 });
}

/** Become a host. Resolves with a Session once a guest connects. */
export function hostRoom({
  onRoomReady,
  onError,
}: {
  onRoomReady?: (code: string) => void;
  onError?: (err: unknown) => void;
} = {}): Promise<Session> {
  return new Promise((resolve, reject) => {
    const roomCode = generateRoomCode();
    const peer = makePeer(ROOM_PREFIX + roomCode);
    let session: Session | null = null;
    peer.on("open", () => onRoomReady?.(roomCode));
    peer.on("connection", (conn) => {
      conn.on("open", () => {
        if (!session) {
          session = makeSession({ peer, conn, role: "host", roomCode });
          resolve(session);
        } else {
          session._swapConn(conn);
        }
      });
    });
    peer.on("error", (err) => {
      onError?.(err);
      if (!session) reject(err);
    });
  });
}

/** Connect to an existing host by room code. Resolves with a Session. */
export function joinRoom(
  roomCode: string,
  { onError }: { onError?: (err: unknown) => void } = {},
): Promise<Session> {
  return new Promise((resolve, reject) => {
    const peer = makePeer();
    peer.on("open", () => {
      // reliable:true → an ORDERED data channel: moves/over/rematch messages
      // must not overtake one another.
      const conn = peer.connect(ROOM_PREFIX + roomCode.toUpperCase(), {
        reliable: true,
      });
      conn.on("open", () =>
        resolve(makeSession({ peer, conn, role: "guest", roomCode })),
      );
      conn.on("error", (err) => {
        onError?.(err);
        reject(err);
      });
    });
    peer.on("error", (err) => {
      onError?.(err);
      reject(err);
    });
  });
}

/** One bounded guest reconnect attempt → Session or null (always cleans up its peer). */
export function tryJoinRoom(
  roomCode: string,
  timeoutMs = 7000,
): Promise<Session | null> {
  return new Promise((resolve) => {
    let peer: Peer | undefined;
    let settled = false;
    const fail = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      try {
        peer?.destroy();
      } catch {
        /* */
      }
      resolve(null);
    };
    const timer = window.setTimeout(fail, timeoutMs);
    try {
      peer = makePeer();
    } catch {
      fail();
      return;
    }
    peer.on("open", () => {
      const conn = peer!.connect(ROOM_PREFIX + roomCode.toUpperCase(), {
        reliable: true,
      });
      conn.on("open", () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(makeSession({ peer: peer!, conn, role: "guest", roomCode }));
      });
      conn.on("error", fail);
    });
    peer.on("error", fail);
  });
}

function makeSession({
  peer,
  conn,
  role,
  roomCode,
}: {
  peer: Peer;
  conn: DataConnection;
  role: "host" | "guest";
  roomCode: string;
}): Session {
  const handlers = new Set<(msg: unknown) => void>();
  const closeHandlers = new Set<() => void>();
  const lostHandlers = new Set<() => void>();
  const reconnectHandlers = new Set<() => void>();
  let closed = false;
  let lost = false;

  const fireClose = () => {
    if (closed) return;
    closed = true;
    for (const h of closeHandlers) h();
  };
  const fireLost = () => {
    if (closed || lost) return;
    lost = true;
    for (const h of lostHandlers) h();
  };

  const attachConn = (c: DataConnection) => {
    c.on("data", (data) => {
      for (const h of handlers) h(data);
    });
    c.on("close", fireLost);
    c.on("error", fireLost);
  };
  attachConn(conn);

  peer.on("disconnected", () => {
    if (closed) return;
    try {
      peer.reconnect();
    } catch {
      /* */
    }
  });

  return {
    role,
    roomCode,
    get peerId() {
      return peer.id;
    },
    get remoteId() {
      return conn.peer;
    },
    send(msg) {
      if (closed || lost) return;
      try {
        conn.send(msg);
      } catch {
        /* */
      }
    },
    onMessage(h) {
      handlers.add(h);
      return () => handlers.delete(h);
    },
    onClose(h) {
      closeHandlers.add(h);
      return () => closeHandlers.delete(h);
    },
    onLost(h) {
      lostHandlers.add(h);
      return () => lostHandlers.delete(h);
    },
    onReconnect(h) {
      reconnectHandlers.add(h);
      return () => reconnectHandlers.delete(h);
    },
    isOpen() {
      return !closed && !lost;
    },
    isLost() {
      return lost;
    },
    _swapConn(newConn) {
      if (closed) return;
      try {
        conn.close();
      } catch {
        /* */
      }
      conn = newConn;
      lost = false;
      attachConn(newConn);
      for (const h of reconnectHandlers) h();
    },
    close() {
      try {
        conn.close();
      } catch {
        /* */
      }
      try {
        peer.destroy();
      } catch {
        /* */
      }
      fireClose();
    },
    dispose() {
      closed = true;
      handlers.clear();
      closeHandlers.clear();
      lostHandlers.clear();
      reconnectHandlers.clear();
      try {
        conn.close();
      } catch {
        /* */
      }
      try {
        peer.destroy();
      } catch {
        /* */
      }
    },
  };
}
